import React, { useState } from "react";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { postFolder } from "../api/folderapi";

const CreateView = ({
  path,
  parentId,
  setIsCreateModalOpen,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [fileInput, setFileInput] = useState([]);
  const [fileType, setFileType] = useState("folder");
  const [name, setName] = useState("");
  const navigate = useNavigate();


const handleCreate = async () => {
  setIsLoading(true);

  // 1️⃣ VALIDATION → FileType required
  if (!fileType) {
    toast.error("Please select File or Folder");
    setIsLoading(false);
    return;
  }

  // 2️⃣ FILE VALIDATION
  if (fileType === "file") {
    if (!fileInput || fileInput.length === 0) {
      toast.error("Please select at least one file");
      setIsLoading(false);
      return;
    }

    const uploads = Array.from(fileInput).map((file) => {
      const formData = new FormData();
      formData.append("name", file.name);
      formData.append("type", "file");
      formData.append("file", file);
      formData.append("path", path || "/");
      if (parentId) formData.append("parentId", parentId);

      return postFolder(formData, setProgress);
    });

    try {
      await Promise.all(uploads);
      toast.success("Files uploaded successfully");
      setIsCreateModalOpen(false);
    } catch (err) {
      toast.error("Failed to upload files");
      console.error(err);
    } finally {
      setIsLoading(false);
    }

    return;
  }

  // 3️⃣ FOLDER VALIDATION
  if (fileType === "folder" && !name.trim()) {
    toast.error("Please enter folder name");
    setIsLoading(false);
    return;
  }

  // 4️⃣ FOLDER CREATE
  const formData = new FormData();
  formData.append("name", name);
  formData.append("type", "folder");
  formData.append("path", path || "/");
  if (parentId) formData.append("parentId", parentId);

  try {
    await postFolder(formData, setProgress);
    toast.success("Folder created successfully");
    setIsCreateModalOpen(false);
  } catch (err) {
    toast.error("Failed to create folder");
    console.error(err);
  } finally {
    setIsLoading(false);
  }
};




  return (
    <div className="fixed inset-0  z-50  flex items-center justify-center bg-black bg-opacity-50">
      <div className="">
<div className="bg-white p-5 w-80 md:p-8 rounded-xl md:w-[520px] relative shadow-lg">

  {/* LOADING OVERLAY */}
{isLoading && (
  <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/50 backdrop-blur-sm rounded-xl">
    <div className="bg-white/80 backdrop-blur-md p-6 rounded-2xl shadow-xl flex flex-col items-center w-72">
      
      {/* Spinner */}
      <div className="flex space-x-1 mb-4">
        <div className="w-3 h-3 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
        <div className="w-3 h-3 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
        <div className="w-3 h-3 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
      </div>

      <p className="text-gray-700 font-semibold">Uploading...</p>

      {/* Progress Bar */}
      <div className="w-full h-2 bg-gray-200 rounded overflow-hidden mt-3">
        <div
          className="h-2 bg-blue-500 rounded-full transition-all duration-300"
          style={{ width: `${progress}%` }}
        />
      </div>
      <p className="text-sm text-gray-500 mt-2">{progress}%</p>
    </div>
  </div>
)}




  {/* TITLE */}
  <h2 className="text-xl font-semibold mb-4">Create New</h2>

  {/* SELECT TYPE */}
  <select
    className="w-full p-3 border rounded-lg mb-4 bg-gray-50"
    onChange={(e) => {
      setFileType(e.target.value);
      setFileInput([]);
      setName("");
    }}
    value={fileType}
  >
    <option value="folder">Folder</option>
    <option value="file">File</option>
  </select>

  {/* FILE UPLOAD UI */}
  {fileType === "file" ? (
    <>
      {/* DROPZONE */}
      <label
        htmlFor="fileUpload"
        className="border-2 border-dashed border-secondary-300 bg-blue-50 hover:bg-blue-100 transition cursor-pointer rounded-xl p-6 flex flex-col justify-center items-center text-center"
      >
        <img src="/upload-cloud.png" className="w-10 opacity-80 mb-2" />
        <p className="text-gray-600">Drag & drop or click to choose files</p>
        <p className="text-xs text-gray-400 mt-1">Supported formats: XLS, XLSX</p>
      </label>

      <input
        id="fileUpload"
        type="file"
        multiple
        className="hidden"
        onChange={(e) => setFileInput(e.target.files)}
      />

      {/* FILE PREVIEW LIST */}
      <div className="mt-4 space-y-3 max-h-40 overflow-y-auto pr-1">
        {Array.from(fileInput).map((file, index) => (
          <div
            key={index}
            className="flex items-center justify-between p-3 border rounded-lg bg-gray-50"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gray-200 rounded flex items-center justify-center text-lg">
                📄
              </div>
              <div>
                <p className="text-sm font-medium">{file.name}</p>
                <p className="text-xs text-gray-400">{(file.size / 1024 / 1024).toFixed(1)} MB</p>
              </div>
            </div>
            <button
              className="text-red-500 text-xl"
              onClick={() => {
                const arr = [...fileInput];
                arr.splice(index, 1);
                setFileInput(arr);
              }}
            >
              ✕
            </button>
          </div>
        ))}
      </div>
    </>
  ) : (
    /* FOLDER NAME INPUT */
    <input
      type="text"
      value={name}
      className="w-full p-3 border rounded-lg bg-gray-50"
      placeholder="Enter Folder Name"
      onChange={(e) => setName(e.target.value)}
    />
  )}

  {/* BUTTONS */}
  <div className="flex justify-between gap-3 mt-10">
    <button
      className="px-4 py-2 bg-gray-200 font-semibold rounded-lg"
      onClick={() => {
        setIsCreateModalOpen(false);
        setFileInput([]);
      }}
    >
      Cancel
    </button>

    <button
      className="px-4 py-2 bg-third font-semibold  text-primary rounded-lg "
      onClick={handleCreate}
      disabled={fileType === "file" && fileInput.length === 0}
    >
      Create
    </button>
  </div>
</div>


       
      </div>
    </div>
  );
};

export default CreateView;
 